import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import dashboardModuleService from "../../../services/dashboardModuleService";
import { instanceToken } from "../../../utils/constant";
import { SECTION_ORDER } from "../../../config/rolePermissions";
import { ICON_KEY_OPTIONS } from "../../../config/moduleIcons";
import { getRegisteredModuleKeys } from "../../../config/routeRegistry";

const moduleKeyHint =
  "camelCase: starts with lowercase letter, then letters/digits; 2-64 chars";

const emptyCreateForm = () => ({
  key: "",
  label: "",
  section: SECTION_ORDER[0],
  base_path: "",
  icon_key: ICON_KEY_OPTIONS[0] || "viewModule",
  sort_order: 0,
});

export default function DashboardModules({ homeAlert }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [editForm, setEditForm] = useState(null);

  const registeredKeys = useMemo(
    () => getRegisteredModuleKeys(homeAlert),
    [homeAlert]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await dashboardModuleService.getAdminCatalog(
        instanceToken.token
      );
      setModules(Array.isArray(res.data.catalog) ? res.data.catalog : []);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error || "Failed to load dashboard modules"
      );
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sectionOptions = useMemo(() => {
    const fromData = modules.map((m) => m.section).filter(Boolean);
    return [...new Set([...SECTION_ORDER, ...fromData])];
  }, [modules]);

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditForm({
      key: row.key,
      label: row.label,
      section: row.section,
      base_path: row.basePath || "",
      icon_key: row.iconKey || "viewModule",
      sort_order: row.sortOrder ?? 0,
      active: row.active !== false,
      system: row.system,
    });
    setEditOpen(true);
  };

  const handleCreate = async () => {
    const key = createForm.key.trim();
    const label = createForm.label.trim();
    const section = createForm.section.trim();
    if (!key || !label || !section) {
      homeAlert?.("Key, label, and section are required", true);
      return;
    }

    setSaving(true);
    try {
      await dashboardModuleService.createModule(instanceToken.token, {
        key,
        label,
        section,
        base_path: createForm.base_path.trim() || null,
        icon_key: createForm.icon_key || null,
        sort_order: Number(createForm.sort_order) || 0,
      });
      homeAlert?.(`Module ${key} created`, false);
      setCreateOpen(false);
      await load();
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || "Failed to create module",
        true
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForm?.key) return;
    setSaving(true);
    try {
      await dashboardModuleService.updateModule(instanceToken.token, editForm.key, {
        label: editForm.label.trim(),
        section: editForm.section.trim(),
        base_path: editForm.base_path.trim() || null,
        icon_key: editForm.icon_key || null,
        sort_order: Number(editForm.sort_order) || 0,
        active: editForm.active,
      });
      homeAlert?.(`Module ${editForm.key} updated`, false);
      setEditOpen(false);
      setEditForm(null);
      await load();
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || "Failed to update module",
        true
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (
      !window.confirm(
        `Delete module "${key}"? This cannot be undone if roles reference it.`
      )
    ) {
      return;
    }
    setDeletingKey(key);
    try {
      await dashboardModuleService.deleteModule(instanceToken.token, key);
      homeAlert?.(`Module ${key} deleted`, false);
      await load();
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || `Failed to delete ${key}`,
        true
      );
    } finally {
      setDeletingKey(null);
    }
  };

  const renderTypeChip = (system) =>
    system ? (
      <Chip label="System" size="small" variant="outlined" />
    ) : (
      <Chip label="Custom" size="small" color="secondary" variant="outlined" />
    );

  const renderRouteChip = (row) => {
    if (!row.basePath) {
      return <Chip label="No path" size="small" variant="outlined" />;
    }
    if (!registeredKeys.has(row.key)) {
      return (
        <Chip label="No route" size="small" color="warning" variant="outlined" />
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Dashboard Modules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage module catalog for role permissions. New modules appear in
            Role Permissions immediately; sidebar routes still require a
            developer deploy.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>
          Create Module
        </Button>
      </Box>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Base Path</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Icon</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Route</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((row) => (
              <TableRow key={row.key} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{row.key}</TableCell>
                <TableCell>{row.label}</TableCell>
                <TableCell>{row.section}</TableCell>
                <TableCell>{row.basePath || "—"}</TableCell>
                <TableCell>{row.iconKey || "—"}</TableCell>
                <TableCell>{renderTypeChip(row.system)}</TableCell>
                <TableCell>
                  {row.active === false ? (
                    <Chip label="Inactive" size="small" color="default" />
                  ) : (
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{renderRouteChip(row)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button size="small" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                    {!row.system ? (
                      <LoadingButton
                        size="small"
                        color="error"
                        loading={deletingKey === row.key}
                        onClick={() => handleDelete(row.key)}
                      >
                        Delete
                      </LoadingButton>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Module</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Module Key"
              value={createForm.key}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, key: e.target.value }))
              }
              helperText={moduleKeyHint}
              placeholder="e.g. financeReports"
              fullWidth
            />
            <TextField
              label="Label"
              value={createForm.label}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, label: e.target.value }))
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                label="Section"
                value={createForm.section}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, section: e.target.value }))
                }
              >
                {sectionOptions.map((section) => (
                  <MenuItem key={section} value={section}>
                    {section}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Base Path (optional)"
              value={createForm.base_path}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, base_path: e.target.value }))
              }
              placeholder="/finance-reports"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                label="Icon"
                value={createForm.icon_key}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, icon_key: e.target.value }))
                }
              >
                {ICON_KEY_OPTIONS.map((iconKey) => (
                  <MenuItem key={iconKey} value={iconKey}>
                    {iconKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sort Order"
              type="number"
              value={createForm.sort_order}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  sort_order: e.target.value,
                }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleCreate}>
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Module — {editForm?.key}</DialogTitle>
        <DialogContent>
          {editForm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Label"
                value={editForm.label}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, label: e.target.value }))
                }
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Section</InputLabel>
                <Select
                  label="Section"
                  value={editForm.section}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, section: e.target.value }))
                  }
                >
                  {sectionOptions.map((section) => (
                    <MenuItem key={section} value={section}>
                      {section}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Base Path"
                value={editForm.base_path}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, base_path: e.target.value }))
                }
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  label="Icon"
                  value={editForm.icon_key}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, icon_key: e.target.value }))
                  }
                >
                  {ICON_KEY_OPTIONS.map((iconKey) => (
                    <MenuItem key={iconKey} value={iconKey}>
                      {iconKey}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Sort Order"
                type="number"
                value={editForm.sort_order}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    sort_order: e.target.value,
                  }))
                }
                fullWidth
              />
              {!editForm.system ? (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={editForm.active ? "active" : "inactive"}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        active: e.target.value === "active",
                      }))
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleUpdate}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
